import { useState, useMemo } from "react";
import { ArrowRight, ExternalLink, Check, Search } from "lucide-react";
import { PublicNav, PublicFooter } from "@/components/public/PublicChrome";

/* ---------- Data (public directory · SANDBOX) ---------- */
const TABS = [
  {
    id: "cards", label: "Business Credit Cards",
    eyebrow: "Marketplace · Cards", title: "Business Credit Cards",
    sub: "Business cards, store cards, fleet cards and secured lines from the public directory our specialists use — including which bureaus each one reports to.",
    layout: "cards",
    sections: [
      {
        title: "Business credit cards", cards: [
          { name: "Divvy (BILL)", sub: "Free · no personal guarantee for larger biz", reports: "D&B, Equifax Business", level: "Medium", clicks: 1 },
          { name: "Ramp", sub: "Corporate card, no PG option", reports: "D&B, Experian Business", level: "Medium" },
          { name: "Brex", sub: "$50K bank balance recommended", reports: "D&B, Experian Business", level: "Medium" },
          { name: "Amex Business Gold", sub: "$375/yr · personal credit pull", reports: "D&B, Experian Business", level: "SBFE" },
          { name: "Capital One Spark", sub: "Cashback · personal credit pull", reports: "D&B, Experian Business", level: "SBFE" },
          { name: "Chase Ink Business", sub: "Solid points · personal credit pull", reports: "D&B, Experian Business", level: "SBFE" },
          { name: "Wells Fargo Business Platinum", sub: "Personal credit pull · established biz", reports: "D&B, Experian Business", level: "SBFE" },
          { name: "Bank of America Business Advantage", sub: "Requires 12+ mo in business", reports: "D&B, Experian Business", level: "Medium" },
          { name: "Amazon Business Line of Credit", sub: "Free · pay-in-full or revolving", reports: "D&B, Equifax Business", level: "Easy" },
          { name: "Bluevine Business Checking", sub: "Bank foundation · no monthly fee", level: "Easy" },
          { name: "Mercury Bank", sub: "Bank foundation · free biz account", level: "Easy" },
          { name: "Novo Bank", sub: "Bank foundation · free biz account", level: "Easy" },
        ],
      },
      {
        title: "Store & vendor credit cards", cards: [
          { name: "Home Depot Commercial Account", sub: "Free · Net-30 store credit", reports: "D&B", level: "Easy" },
          { name: "Lowe's Business Account", sub: "Free · Net-20 to Net-55", reports: "D&B", level: "Easy" },
          { name: "Menards Contractor Card", sub: "Free · Net-30", level: "Easy" },
          { name: "Staples Business Advantage", sub: "Free · Net-30 store", reports: "D&B, Experian Business", level: "Easy" },
          { name: "Costco Business", sub: "Membership + Anywhere Visa", reports: "D&B", level: "Easy" },
          { name: "Sam's Club Business", sub: "Business Mastercard", reports: "D&B", level: "Easy" },
          { name: "Walmart Business Account", sub: "Free · Net-30", reports: "D&B", level: "Easy" },
          { name: "Dell Business Credit", sub: "Tech financing", reports: "D&B, Experian Business", level: "Medium" },
          { name: "Apple Financial Services", sub: "Tech financing · Net-30", reports: "D&B", level: "Medium" },
          { name: "Microsoft Store Business", sub: "Free · Net-30 store credit", reports: "D&B", level: "Easy" },
          { name: "Best Buy Business", sub: "Free · Net-30 store credit", reports: "D&B", level: "Easy" },
        ],
      },
      {
        title: "Fleet & fuel cards", cards: [
          { name: "BP Business Fleet Card", sub: "Free · fuel-only tradeline", reports: "D&B, Experian Business", level: "Easy" },
          { name: "Shell Small Business", sub: "Free · fuel + maintenance", reports: "D&B, Experian Business", level: "Easy" },
          { name: "WEX Fleet Card", sub: "Free · multi-brand fuel", reports: "D&B, Experian Business", level: "Easy" },
          { name: "Fuelman", sub: "Free · nationwide fuel", reports: "D&B, Equifax Business", level: "Easy" },
          { name: "ExxonMobil BusinessPro", sub: "Free · fuel + convenience", reports: "D&B", level: "Easy" },
        ],
      },
      {
        title: "Secured business lines", cards: [
          { name: "Fundbox Line of Credit", sub: "Up to $150K · connects to accounting", reports: "D&B", level: "Easy" },
          { name: "Bluevine Line of Credit", sub: "Up to $250K · fast decisions", reports: "D&B, Experian Business", level: "Medium" },
          { name: "Kabbage (Amex)", sub: "Up to $250K · monthly access line", reports: "D&B", level: "Medium" },
          { name: "OnDeck Term Loan", sub: "Up to $250K · fast funding", reports: "D&B, Experian Business", level: "Hard" },
          { name: "SBA 7(a) Loan", sub: "Up to $5M · government-backed", reports: "D&B, Equifax Business, Experian Business", level: "Hard" },
        ],
      },
      { title: "Cards added by our team", cards: [], empty: "Nothing added here yet — our team lists providers only after reviewing them. Coming soon." },
    ],
  },
  {
    id: "loans", label: "Business Loans & Capital",
    eyebrow: "Marketplace · Funding", title: "Business Loans & Capital",
    sub: "Understand the loan types first, then let a funding specialist match your business — we never send you to a lender blind.",
    layout: "loans",
    sections: [
      {
        title: "Loan types explained", cards: [
          { name: "SBA 7(a) Loan", desc: "Government-backed term loans through approved lenders for working capital, equipment or acquisitions. Longer terms, more paperwork.", best: "Established businesses with 2+ years of financials" },
          { name: "Business Term Loan", desc: "A lump sum repaid on a fixed schedule. Banks and online lenders; pricing follows your business and personal credit profile.", best: "One-time investments with predictable payback" },
          { name: "Business Line of Credit", desc: "Revolving access to funds you draw as needed and repay. Good for cash-flow gaps and seasonal swings.", best: "Recurring short-term needs" },
          { name: "Equipment Financing", desc: "The equipment itself secures the loan or lease, which can make approval easier than unsecured credit.", best: "Vehicles, machinery, tech" },
          { name: "Invoice Factoring", desc: "Advance against outstanding invoices from creditworthy customers. Speeds up cash without new debt on your balance sheet.", best: "B2B businesses with slow-paying customers" },
          { name: "Merchant Cash Advance", desc: "Fast funding sized to monthly revenue. Faster and easier — and typically the most expensive money on this page.", best: "Short-term needs when speed matters" },
        ],
      },
    ],
  },
  {
    id: "checking", label: "Business Checking",
    eyebrow: "Marketplace · Banking", title: "Business Checking",
    sub: "Separate business banking is step one of a real business credit file. Eight banks from our public directory, with the trade-offs spelled out.",
    layout: "banks",
    sections: [
      {
        title: "Banks in our directory", cards: [
          { name: "Chase for Business", badge: "Traditional Bank", best: "established businesses ready to build a lender relationship", bullets: ["Nationwide branch access", "Chase Ink business card ecosystem", "SBA 7(a) preferred lender"], watch: "Monthly fees on lower-tier checking (waivable with minimum balance)", clicks: 1 },
          { name: "Bank of America Business", badge: "Traditional Bank", best: "high-volume businesses that want cashback on everything", bullets: ["Preferred Rewards for Business tiers", "Large branch and ATM network"], watch: "Business checking has monthly fees unless minimums are hit" },
          { name: "Wells Fargo Business", badge: "Traditional Bank", best: "owners who want branches plus a full business credit suite", bullets: ["Business checking, cards and lines under one roof", "Nationwide branches"], watch: "Fees and minimums vary by account tier" },
          { name: "Novo", badge: "Online Bank", best: "freelancers and new LLCs that want no-fee checking", bullets: ["No monthly fees, no minimum balance", "Invoicing and integrations built in"], watch: "No cash deposits at branches" },
          { name: "Bluevine", badge: "Online Bank", best: "businesses that want interest on checking", bullets: ["Interest-bearing business checking", "Line of credit available to qualified businesses"], watch: "Online only" },
          { name: "Mercury", badge: "Online Bank", best: "startups and tech-enabled companies", bullets: ["Free business checking, no monthly fees, no minimums", "Strong software and API tooling"], watch: "Not for sole proprietors" },
          { name: "Relay Financial", badge: "Online Bank", best: "owners who budget with multiple accounts (Profit First)", bullets: ["Up to 20 checking accounts", "Team debit cards with controls"], watch: "Online only" },
          { name: "Live Oak Bank", badge: "SBA Specialist", best: "SBA-backed growth capital", bullets: ["Top SBA 7(a) lender by volume", "High-yield business savings"], watch: "Not a day-to-day checking relationship for most owners" },
        ],
      },
    ],
  },
  {
    id: "vendors", label: "Net-30+ Vendors",
    eyebrow: "Marketplace · Tradelines", title: "Net-30+ Vendors",
    sub: "Starter vendor accounts that report payment history to the business bureaus. Open, order, pay early — that's how a PAYDEX gets built.",
    layout: "vendors",
    sections: [
      {
        title: "Net-30 vendors that report", cards: [
          { name: "Uline", sub: "Free account · 30 days terms", reports: "D&B", level: "Easy" },
          { name: "Grainger", sub: "Free account · 30 days terms", reports: "D&B, Experian Business", level: "Easy" },
          { name: "Quill", sub: "Free · Net-30 after first order", reports: "D&B, Experian Business", level: "Easy" },
          { name: "Summa Office Supplies", sub: "Free account · Net-30", reports: "D&B, Equifax Business", level: "Easy" },
          { name: "Crown Office Supplies", sub: "$99 annual · Net-30", reports: "D&B, Equifax Business, Experian Business", level: "Easy" },
          { name: "Wise Business Plans", sub: "Net-30 terms", reports: "D&B, Equifax Business", level: "Easy" },
          { name: "Strategic Network Solutions", sub: "$75 annual · Net-30", reports: "D&B, Equifax Business", level: "Easy" },
          { name: "eCredable Lift", sub: "$19.95/mo · reports rent + bills", reports: "Equifax Business", level: "Easy", clicks: 1 },
          { name: "Nav Prime", sub: "$49.99/mo · Prime card + tradeline", reports: "D&B, Equifax Business, Experian Business", level: "Easy" },
          { name: "FedEx Small Business", sub: "Free · Net-15 to Net-30", reports: "D&B", level: "Easy" },
          { name: "UPS Business Account", sub: "Free · weekly invoice terms", reports: "D&B", level: "Easy" },
          { name: "DHL Business Account", sub: "Free · Net-30 shipping account", reports: "D&B", level: "Easy" },
          { name: "Comcast Business", sub: "Utility tradeline · on-time bill reporting", reports: "D&B", level: "Easy" },
          { name: "AT&T Business", sub: "Utility tradeline", reports: "D&B", level: "Easy" },
          { name: "Verizon Business", sub: "Utility tradeline", reports: "D&B", level: "Easy" },
          { name: "Bench Accounting", sub: "Bookkeeping · required for funding", level: "Easy" },
          { name: "D&B CreditBuilder Plus", sub: "Direct D&B enrollment · $79/mo", reports: "D&B", level: "Easy" },
        ],
      },
      { title: "Vendors added by our team", cards: [], empty: "Nothing added here yet — our team lists providers only after reviewing them. Coming soon." },
    ],
  },
];

/* ---------- UI pieces ---------- */
function Tag({ children, muted }) {
  return <span className={`rounded-md px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider ${muted ? "bg-slate-100 text-slate-500" : "bg-slate-100 text-slate-500"}`}>{children}</span>;
}

function VisitBtn({ testid, clicks }) {
  return (
    <div className="mt-4 flex items-center justify-between">
      <button data-testid={testid} className="inline-flex items-center gap-2 rounded-full bg-[#0f172a] px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#b0862f]">
        Visit Provider <ExternalLink size={13} />
      </button>
      {clicks ? <span className="font-mono text-[10px] text-slate-400">{clicks} member click{clicks > 1 ? "s" : ""}</span> : null}
    </div>
  );
}

function ProviderCard({ c }) {
  return (
    <div className="flex flex-col rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm transition-shadow hover:shadow-md" data-testid="marketplace-card">
      <div className="font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-[#a37d2a]">{c.name}</div>
      <div className="mt-1.5 font-head text-[15px] font-bold text-slate-900">{c.name}</div>
      <div className="mt-1 text-[13px] text-slate-500">{c.sub}</div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {c.reports && <Tag>Reports to {c.reports}</Tag>}
        {c.level && <Tag>{c.level}</Tag>}
      </div>
      <VisitBtn testid={`visit-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} clicks={c.clicks} />
    </div>
  );
}

function LoanCard({ c }) {
  return (
    <div className="flex flex-col rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm transition-shadow hover:shadow-md" data-testid="marketplace-card">
      <div className="font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-[#a37d2a]">Millionaire Mindsetters</div>
      <div className="mt-2 font-head text-lg font-bold text-slate-900">{c.name}</div>
      <p className="mt-3 text-[14px] leading-relaxed text-slate-600">{c.desc}</p>
      <p className="mt-3 text-[13px] text-slate-700"><span className="font-bold">Best for:</span> {c.best}</p>
      <div className="mt-3"><Tag>Loan Type</Tag></div>
      <div className="mt-4 border-t border-slate-100 pt-4">
        <button data-testid={`match-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-wider text-[#a37d2a] transition-all hover:gap-3">Get Matched <ArrowRight size={14} /></button>
      </div>
    </div>
  );
}

function BankCard({ c }) {
  return (
    <div className="flex flex-col rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm transition-shadow hover:shadow-md" data-testid="marketplace-card">
      <div className="flex items-start justify-between gap-3">
        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-[#a37d2a]">{c.name}</div>
        <span className="shrink-0 rounded-full border border-slate-300 px-2.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wider text-slate-500">{c.badge}</span>
      </div>
      <div className="mt-1.5 font-head text-[17px] font-bold text-slate-900">{c.name}</div>
      <p className="mt-2 text-[13px] text-slate-600"><span className="font-semibold">Best for:</span> {c.best}</p>
      <ul className="mt-3 space-y-1.5">
        {c.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 text-[13px] text-slate-700"><Check size={14} className="mt-0.5 shrink-0 text-[#a37d2a]" />{b}</li>
        ))}
      </ul>
      <p className="mt-3 text-[12px] text-[#a37d2a]"><span className="font-semibold">Watch out:</span> {c.watch}</p>
      <div className="mt-3"><Tag>{c.badge}</Tag></div>
      <VisitBtn testid={`visit-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} clicks={c.clicks} />
    </div>
  );
}

function Section({ section, layout, query }) {
  const filtered = section.cards.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()));
  const isTeam = section.cards.length === 0;
  if (!isTeam && query && filtered.length === 0) return null;
  const Card = layout === "loans" ? LoanCard : layout === "banks" ? BankCard : ProviderCard;
  return (
    <div className="mt-12" data-testid="marketplace-section">
      <div className="flex items-end justify-between border-b border-slate-200 pb-3">
        <h2 className="font-serif-display text-2xl font-semibold text-slate-900 sm:text-3xl">{section.title}</h2>
        <span className="font-mono text-[11px] text-slate-400">{isTeam ? 0 : filtered.length} listings</span>
      </div>
      {isTeam ? (
        <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-white/60 p-6 text-center text-[13px] text-slate-500">{section.empty}</div>
      ) : (
        <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c) => <Card key={c.name} c={c} />)}
        </div>
      )}
    </div>
  );
}

export default function Marketplace() {
  const [tabId, setTabId] = useState("cards");
  const [query, setQuery] = useState("");
  const tab = TABS.find((t) => t.id === tabId);
  const total = useMemo(() => tab.sections.reduce((s, sec) => s + sec.cards.length, 0), [tab]);

  return (
    <div className="min-h-screen bg-[#f7f4ec]" data-testid="marketplace-page">
      <PublicNav />

      {/* Hero */}
      <div className="mx-auto max-w-7xl px-5 pt-12 lg:px-8">
        <div className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#a37d2a]">{tab.eyebrow}</div>
        <h1 className="mt-3 font-serif-display text-5xl font-semibold tracking-tight text-slate-900 lg:text-6xl">{tab.title}</h1>
        <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-slate-500">{tab.sub}</p>

        {/* Tabs + search */}
        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2.5">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => { setTabId(t.id); setQuery(""); }}
                data-testid={`marketplace-tab-${t.id}`}
                className={`rounded-full px-4 py-2 text-[13px] font-semibold uppercase tracking-wide transition-colors ${
                  t.id === tabId ? "bg-[#0f172a] text-[#d8b25a]" : "border border-slate-300 bg-white text-slate-600 hover:border-slate-400"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-80">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="marketplace-search"
              placeholder={`Search ${total} listings...`}
              className="w-full rounded-full border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-[13px] text-slate-800 outline-none placeholder:text-slate-400 focus:border-[#b0862f]"
            />
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="mx-auto max-w-7xl px-5 pb-20 lg:px-8">
        {tab.sections.map((sec) => <Section key={sec.title} section={sec} layout={tab.layout} query={query} />)}
      </div>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
